import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { ConditionalRendering } from '../../../../components/common/conditional-rendering';
import ErrorMessage from '../../../../components/common/form/errorMessage';
import { NUMBER } from '../../../../constants/common/number';
import { RELATIONSHIP_TYPES } from '../../../../constants/common/relationShipTypes';
import DatePicker from '../../../../lib/inputs/react-datepicker';
import { DATE_FORMAT, formatDateToMMDDYYYY } from '../../../../utils/dates/dateMethods';

import { EnrollmentContext } from '../context/MemberMyEnrollmentProvider';
import DetailedFields from '../EnrollmentListing/DetailedFields';

const StyledRowFluid = styled.div<{
    isEditMode: boolean;
    memberEnrlmntStrtenddt: boolean;
    memberEnrlmntStrtenddtEditable: boolean;
}>`
    padding: 1rem;
    display: ${({ isEditMode, memberEnrlmntStrtenddt, memberEnrlmntStrtenddtEditable }) =>
        !isEditMode || memberEnrlmntStrtenddt || memberEnrlmntStrtenddtEditable ? 'block' : 'none'};
`;

const StyledGridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
`;

const StyledGridItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: start;
    > div {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin: 0;
        gap: 0.5rem;
    }

    .gi-input__label {
        padding: 0;
    }
`;

const StyledDates = styled.div`
    display: flex;
    gap: 10px;
    > div {
        width: 100%;
        > div {
            width: fit-content;
        }
    }
`;

const EnrollmentPremiumHistoryDates = () => {
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};
    const { isEdit, enrollmentPremiumFormContext, selectedEnrollment } = useContext(EnrollmentContext);
    const {
        watch,
        setValue,
        formState: { errors },
    } = enrollmentPremiumFormContext;
    const values = watch();

    useEffect(() => {
        if (values.enrollmentBenefitEffectiveStartDateUI && values.enrollmentBenefitEffectiveEndDateUI) {
            const {
                enrollmentBenefitEffectiveStartDateUI: startDateUI,
                enrollmentBenefitEffectiveEndDateUI: endDateUI,
                enrolleeDataDtoList,
            } = values;

            const enrollmentBenefitEffectiveStartMonth = parseInt(
                startDateUI?.substring(NUMBER.ZERO, NUMBER.TWO),
                NUMBER.TEN,
            );
            const enrollmentBenefitEffectiveEndMonth = parseInt(
                endDateUI?.substring(NUMBER.ZERO, NUMBER.TWO),
                NUMBER.TEN,
            );

            // Update enrollee data with effective dates
            const updatedEnrolleeDataDtoList = enrolleeDataDtoList.map(enrollee =>
                enrollee.relationshipToSubscriber.toUpperCase() === RELATIONSHIP_TYPES.SELF.value
                    ? { ...enrollee, enrolleeEffectiveStartDateUI: startDateUI, enrolleeEffectiveEndDateUI: endDateUI }
                    : enrollee,
            );

            setValue('enrolleeDataDtoList', updatedEnrolleeDataDtoList, { shouldDirty: true, shouldValidate: true });

            // Update start month if it has changed
            if (enrollmentBenefitEffectiveStartMonth !== values.enrollmentBenefitEffectiveStartMonth) {
                setValue('enrollmentBenefitEffectiveStartMonth', enrollmentBenefitEffectiveStartMonth, {
                    shouldDirty: true,
                    shouldValidate: true,
                });
            }

            // Update end month if it has changed
            if (enrollmentBenefitEffectiveEndMonth !== values.enrollmentBenefitEffectiveEndMonth) {
                setValue('enrollmentBenefitEffectiveEndMonth', enrollmentBenefitEffectiveEndMonth, {
                    shouldDirty: true,
                    shouldValidate: true,
                });
            }
        }
    }, [values.enrollmentBenefitEffectiveStartDateUI, values.enrollmentBenefitEffectiveEndDateUI]);

    const onChangBenefitEffectiveDate = (field: string, date: Date | null) => {
        setValue(field, formatDateToMMDDYYYY(date) || '', { shouldDirty: true, shouldValidate: true });
    };

    return (
        <StyledRowFluid
            isEditMode={isEdit}
            memberEnrlmntStrtenddt={userPermissions?.memberEnrlmntStrtenddt}
            memberEnrlmntStrtenddtEditable={userPermissions?.memberEnrlmntStrtenddtEditable}
        >
            <StyledGridContainer>
                <ConditionalRendering isTrue={!isEdit}>
                    <StyledDates>
                        <DetailedFields
                            label="member.enrollment_list.premiumhistory.enrollee.enrollmentStartDate"
                            value={selectedEnrollment?.enrollmentBenefitEffectiveStartDateUI}
                            dataTestId="enrollmentStartDate "
                            showColon={false}
                        />
                    </StyledDates>

                    <StyledDates>
                        <DetailedFields
                            label="member.enrollment_list.premiumhistory.enrollee.enrollmentEndDate"
                            value={selectedEnrollment?.enrollmentBenefitEffectiveEndDateUI}
                            dataTestId="enrollmentStartDate "
                            showColon={false}
                        />
                    </StyledDates>
                </ConditionalRendering>
                <ConditionalRendering isTrue={isEdit}>
                    <StyledGridItem>
                        <DatePicker
                            labelText="member.enrollment_list.premiumhistory.enrollee.enrollmentStartDate"
                            wrapperClasses={['date-picker']}
                            isRequired
                            id="enrollmentStartDate"
                            type="text"
                            pattern={DATE_FORMAT}
                            value={
                                values?.enrollmentBenefitEffectiveStartDateUI
                                    ? new Date(values.enrollmentBenefitEffectiveStartDateUI)
                                    : ''
                            }
                            withMask
                            disabled={!userPermissions?.memberEnrlmntStrtenddtEditable}
                            onDateChange={value =>
                                onChangBenefitEffectiveDate('enrollmentBenefitEffectiveStartDateUI', value)
                            }
                        />
                        <ErrorMessage
                            isVisible={!!errors.enrollmentBenefitEffectiveStartDateUI}
                            messageText={errors?.enrollmentBenefitEffectiveStartDateUI?.message}
                        />
                    </StyledGridItem>
                    <StyledGridItem>
                        <DatePicker
                            labelText="member.enrollment_list.premiumhistory.enrollee.enrollmentEndDate"
                            wrapperClasses={['date-picker']}
                            isRequired
                            id="enrollmentEndDate"
                            type="text"
                            pattern={DATE_FORMAT}
                            disabled={!userPermissions?.memberEnrlmntStrtenddtEditable}
                            value={
                                values?.enrollmentBenefitEffectiveEndDateUI
                                    ? new Date(values.enrollmentBenefitEffectiveEndDateUI)
                                    : ''
                            }
                            withMask
                            onDateChange={value =>
                                onChangBenefitEffectiveDate('enrollmentBenefitEffectiveEndDateUI', value)
                            }
                        />
                        <ErrorMessage
                            isVisible={!!errors.enrollmentBenefitEffectiveEndDateUI}
                            messageText={errors?.enrollmentBenefitEffectiveEndDateUI?.message}
                        />
                    </StyledGridItem>
                </ConditionalRendering>
            </StyledGridContainer>
        </StyledRowFluid>
    );
};

export default EnrollmentPremiumHistoryDates;
